import React, { useEffect, useState } from 'react';
import {
  Alert,
  Button,
  Checkbox,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControlLabel,
  Stack,
  Typography,
} from '@mui/material';
import api from '../../api';

interface WaecSubject {
  slug: string;
  name: string;
  departments: string[];
}

interface WaecRegistrationResponse {
  selectedSubjects: string[];
  subjects: WaecSubject[];
}

interface WaecVirtualRegistrationDialogProps {
  open: boolean;
  onClose: () => void;
}

const WaecVirtualRegistrationDialog: React.FC<WaecVirtualRegistrationDialogProps> = ({ open, onClose }) => {
  const [subjects, setSubjects] = useState<WaecSubject[]>([]);
  const [selected, setSelected] = useState<string[]>([]);
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');
  const [saved, setSaved] = useState(false);

  useEffect(() => {
    if (!open) return;
    let active = true;
    setLoading(true);
    setError('');
    api.get('/discussion-rooms/virtual-registration/waec')
      .then((response) => {
        if (!active) return;
        const data = response.data?.data as WaecRegistrationResponse;
        setSubjects(Array.isArray(data?.subjects) ? data.subjects : []);
        setSelected(Array.isArray(data?.selectedSubjects) ? data.selectedSubjects : []);
      })
      .catch((requestError: any) => {
        if (active) setError(requestError?.response?.data?.message || 'Unable to load WAEC virtual subject options.');
      })
      .finally(() => { if (active) setLoading(false); });
    return () => { active = false; };
  }, [open]);

  const toggleSubject = (slug: string) => {
    setSelected((current) => current.includes(slug)
      ? current.filter((entry) => entry !== slug)
      : [...current, slug]);
    setSaved(false);
  };

  const save = async () => {
    setSaving(true);
    setError('');
    try {
      const response = await api.put('/discussion-rooms/virtual-registration/waec', { subjectSlugs: selected });
      setSelected(response.data?.data?.selectedSubjects || selected);
      setSaved(true);
    } catch (requestError: any) {
      setError(requestError?.response?.data?.message || 'Unable to save WAEC virtual subject choices.');
    } finally {
      setSaving(false);
    }
  };

  return (
    <Dialog open={open} onClose={() => !saving && onClose()} fullWidth maxWidth="sm">
      <DialogTitle>WAEC virtual learning subjects</DialogTitle>
      <DialogContent>
        <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
          Choose the WAEC subjects whose virtual meetings and learning resources you want to access. This choice is separate from school assignments.
        </Typography>
        {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}
        {saved && <Alert severity="success" sx={{ mb: 2 }}>Your WAEC virtual subjects have been saved.</Alert>}
        {loading ? <CircularProgress size={26} /> : (
          <Stack spacing={0.5} sx={{ maxHeight: '55vh', overflowY: 'auto' }}>
            {subjects.map((subject) => (
              <FormControlLabel
                key={subject.slug}
                control={<Checkbox checked={selected.includes(subject.slug)} onChange={() => toggleSubject(subject.slug)} />}
                label={(
                  <span>
                    <Typography component="span" variant="body2">{subject.name}</Typography>
                    {subject.departments?.length > 0 && (
                      <Typography component="span" variant="caption" color="text.secondary"> · {subject.departments.join(', ')}</Typography>
                    )}
                  </span>
                )}
              />
            ))}
            {!subjects.length && !error && <Typography color="text.secondary">No WAEC subjects are available for your department.</Typography>}
          </Stack>
        )}
      </DialogContent>
      <DialogActions sx={{ px: 3, pb: 2 }}>
        <Button onClick={onClose} disabled={saving}>Close</Button>
        <Button variant="contained" onClick={() => void save()} disabled={loading || saving || !subjects.length}>
          {saving ? 'Saving…' : 'Save subjects'}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default WaecVirtualRegistrationDialog;
